import type { ComboChoice } from '@/shared/lib/db/schemas/cart.schema';
import { calculateAddonsPrice as calculateCartAddonsPrice } from '@/modules/cart/utils/calculate-addons-price';

import type { AddonCategory, SelectedAddon } from '../types/addons';
import type {
  ComboFixedItem,
  ComboGroup,
  ComboGroupOption,
  StoreItem,
} from '../types/get-store-items';
import {
  checkMaximumRequirements,
  checkMinimumRequirements,
  findAddonById,
  getTotalSelectedInCategory,
} from './addon';
import { parsePrice } from './promotion';

/**
 * Combo configurável (PRAT-20): regras puras da montagem do combo no app.
 *
 * Um combo com `combo_groups` pede ao cliente uma ou mais escolhas por grupo
 * (mín./máx. de escolhas). Em grupo personalizável, cada produto escolhido
 * recebe os próprios complementos, com as MESMAS regras de um produto avulso.
 * Combo sem grupos (fixo) e produto comum não passam por aqui.
 */

/** Escolha em andamento na tela do produto (estado da UI). */
export type ComboSelection = {
  group_id: string;
  item_id: string;
  quantity: number;
  addons: SelectedAddon[];
};

export type ComboValidation = {
  isValid: boolean;
  message: string;
  groupId?: string;
};

/** Linha de `combo_choices` enviada no place-order. */
export type ComboChoicePayload = {
  group_id: string;
  item_id: string;
  quantity: number;
  addons: { id: string; addon_name: string; addon_quantity: number }[];
};

/** `true` quando o item é um combo com grupos de escolha. */
export function hasComboGroups(
  item: Pick<StoreItem, 'combo_groups'> | null | undefined
): item is Pick<StoreItem, 'combo_groups'> & { combo_groups: ComboGroup[] } {
  return Array.isArray(item?.combo_groups) && item.combo_groups.length > 0;
}

/**
 * Produtos que sempre vêm no combo fixo (PRAT-79), para a tela do produto
 * listar "Neste combo". Combo de escolha e produto comum devolvem `[]`.
 */
export function getFixedComboItems(
  item: Pick<StoreItem, 'combo_items' | 'combo_groups' | 'combo_mode'> | null | undefined
): ComboFixedItem[] {
  if (!item || isChoiceCombo(item) || !Array.isArray(item.combo_items)) return [];
  return item.combo_items
    .filter(row => row && typeof row.name === 'string' && row.name.trim() !== '')
    .map(row => ({ ...row, quantity: Math.max(1, Math.floor(Number(row.quantity) || 1)) }));
}

/**
 * `true` quando o item é um combo de escolha (`combo_mode === 'choice'` ou com
 * grupos de escolha) — é esse combo que é montado em etapas.
 */
export function isChoiceCombo(
  item: Pick<StoreItem, 'combo_groups' | 'combo_mode'> | null | undefined
): boolean {
  return item?.combo_mode === 'choice' || hasComboGroups(item);
}

/** Grupos ordenados como o lojista cadastrou. */
export function getSortedComboGroups(groups: ComboGroup[] | null | undefined): ComboGroup[] {
  if (!Array.isArray(groups)) return [];
  return [...groups].sort((a, b) => (a.order_column ?? 0) - (b.order_column ?? 0));
}

export function isComboGroupCustomizable(group: Pick<ComboGroup, 'is_customizable'>): boolean {
  return Boolean(Number(group.is_customizable));
}

/** Acréscimo da opção como número (a API manda string decimal ou número). */
export function getOptionAdditionalPrice(option: Pick<ComboGroupOption, 'additional_price'>) {
  return parsePrice(option.additional_price) ?? 0;
}

function getMinChoices(group: ComboGroup): number {
  const min = Number(group.min_choices);
  return Number.isFinite(min) && min > 0 ? Math.floor(min) : 0;
}

/** Teto de escolhas do grupo; `Infinity` quando o grupo não tem teto (0). */
export function getMaxChoices(group: ComboGroup): number {
  const max = Number(group.max_choices);
  return Number.isFinite(max) && max > 0 ? Math.floor(max) : Infinity;
}

/**
 * Regra legível de uma escolha: "Escolha 1", "Escolha de 1 a 3",
 * "Escolha pelo menos 2", "Opcional, até 2", "Opcional".
 */
function formatChoiceRule(min: number, max: number, required: boolean): string {
  const finite = Number.isFinite(max);

  if (!required) {
    if (!finite) return 'Opcional';
    return min > 0 && min < max ? `Opcional, de ${min} a ${max}` : `Opcional, até ${max}`;
  }

  if (!finite) return `Escolha pelo menos ${min}`;
  return min === max ? `Escolha ${max}` : `Escolha de ${min} a ${max}`;
}

/** Legenda da regra do grupo (ex.: "Escolha 1", "Escolha de 1 a 3"). */
export function getComboGroupRuleLabel(group: ComboGroup): string {
  const min = getMinChoices(group);
  return formatChoiceRule(min, getMaxChoices(group), min > 0);
}

export function isComboGroupRequired(group: ComboGroup): boolean {
  return getMinChoices(group) > 0;
}

/** Quantidade de escolhas feitas no grupo (soma das quantidades). */
export function getGroupSelectedCount(groupId: string, selections: ComboSelection[]): number {
  return selections
    .filter(selection => selection.group_id === groupId)
    .reduce((total, selection) => total + selection.quantity, 0);
}

export function findComboSelection(
  selections: ComboSelection[],
  groupId: string,
  itemId: string
): ComboSelection | undefined {
  return selections.find(
    selection => selection.group_id === groupId && selection.item_id === itemId
  );
}

/** Máximo que a opção pode ter sem estourar o teto do grupo. */
export function getOptionMaxQuantity(
  group: ComboGroup,
  itemId: string,
  selections: ComboSelection[]
): number {
  const others = selections
    .filter(selection => selection.group_id === group.id && selection.item_id !== itemId)
    .reduce((total, selection) => total + selection.quantity, 0);

  const remaining = getMaxChoices(group) - others;
  return Number.isFinite(remaining) ? Math.max(0, remaining) : 99;
}

/**
 * Toque numa opção. Em grupo de escolha única troca a escolha (ou desmarca a
 * atual); nos demais marca/desmarca a opção, respeitando o teto do grupo.
 */
export function toggleComboOption(
  group: ComboGroup,
  itemId: string,
  selections: ComboSelection[]
): ComboSelection[] {
  const current = findComboSelection(selections, group.id, itemId);
  if (current) {
    return selections.filter(selection => selection !== current);
  }

  if (getMaxChoices(group) === 1) {
    return [
      ...selections.filter(selection => selection.group_id !== group.id),
      { group_id: group.id, item_id: itemId, quantity: 1, addons: [] },
    ];
  }

  if (getOptionMaxQuantity(group, itemId, selections) < 1) return selections;

  return [...selections, { group_id: group.id, item_id: itemId, quantity: 1, addons: [] }];
}

/** Define a quantidade de uma opção (0 remove), limitada ao teto do grupo. */
export function setComboOptionQuantity(
  group: ComboGroup,
  itemId: string,
  quantity: number,
  selections: ComboSelection[]
): ComboSelection[] {
  const nextQuantity = Math.min(
    Math.max(0, Math.floor(quantity)),
    getOptionMaxQuantity(group, itemId, selections)
  );
  const current = findComboSelection(selections, group.id, itemId);

  if (nextQuantity === 0) {
    return current ? selections.filter(selection => selection !== current) : selections;
  }

  if (current) {
    return selections.map(selection =>
      selection === current ? { ...selection, quantity: nextQuantity } : selection
    );
  }

  return [
    ...selections,
    { group_id: group.id, item_id: itemId, quantity: nextQuantity, addons: [] },
  ];
}

/** Troca os complementos de uma escolha (grupo personalizável). */
export function setComboChoiceAddons(
  groupId: string,
  itemId: string,
  addons: SelectedAddon[],
  selections: ComboSelection[]
): ComboSelection[] {
  return selections.map(selection =>
    selection.group_id === groupId && selection.item_id === itemId
      ? { ...selection, addons }
      : selection
  );
}

function pluralOption(count: number) {
  return count === 1 ? 'opção' : 'opções';
}

/**
 * Valida a montagem: cada grupo dentro do mín./máx. de escolhas, cada escolha
 * pertencente ao grupo e, em grupo personalizável, os complementos da escolha
 * dentro das regras do produto escolhido.
 */
export function validateComboSelections(
  groups: ComboGroup[],
  selections: ComboSelection[]
): ComboValidation {
  for (const group of getSortedComboGroups(groups)) {
    const groupSelections = selections.filter(selection => selection.group_id === group.id);
    const count = groupSelections.reduce((total, selection) => total + selection.quantity, 0);
    const min = getMinChoices(group);
    const max = getMaxChoices(group);

    if (count < min) {
      return {
        isValid: false,
        message: `${group.name}: escolha ${min === max ? '' : 'pelo menos '}${min} ${pluralOption(min)}`,
        groupId: group.id,
      };
    }

    if (count > max) {
      return {
        isValid: false,
        message: `${group.name}: escolha no máximo ${max} ${pluralOption(max)}`,
        groupId: group.id,
      };
    }

    for (const selection of groupSelections) {
      const option = group.options.find(opt => opt.item_id === selection.item_id);
      if (!option) {
        return {
          isValid: false,
          message: `${group.name}: uma das opções escolhidas não está mais disponível`,
          groupId: group.id,
        };
      }

      if (!isComboGroupCustomizable(group)) continue;

      const categories = option.addon_categories ?? [];
      const minCheck = checkMinimumRequirements(selection.addons, categories);
      if (!minCheck.isValid) {
        return {
          isValid: false,
          message: `${option.name} · ${minCheck.message}`,
          groupId: group.id,
        };
      }

      const maxCheck = checkMaximumRequirements(selection.addons, categories);
      if (!maxCheck.isValid) {
        return {
          isValid: false,
          message: `${option.name} · ${maxCheck.message}`,
          groupId: group.id,
        };
      }
    }
  }

  return { isValid: true, message: '' };
}

/**
 * Converte as escolhas da tela em `ComboChoice` do carrinho, na ordem dos
 * grupos/opções do cardápio. Complementos só entram em grupo personalizável e
 * levam a regra de cobrança da categoria, como os complementos de um item.
 */
export function toCartComboChoices(
  groups: ComboGroup[],
  selections: ComboSelection[]
): ComboChoice[] {
  const choices: ComboChoice[] = [];

  for (const group of getSortedComboGroups(groups)) {
    const customizable = isComboGroupCustomizable(group);

    for (const option of group.options) {
      const selection = findComboSelection(selections, group.id, option.item_id);
      if (!selection || selection.quantity <= 0) continue;

      const categories = option.addon_categories ?? [];

      choices.push({
        group_id: group.id,
        group_name: group.name,
        item_id: option.item_id,
        name: option.name,
        quantity: selection.quantity,
        additional_price: getOptionAdditionalPrice(option),
        addons: customizable
          ? selection.addons.map(addon => {
              const addonData = findAddonById(addon.id, categories);
              const categoryId = addon.addon_category_id || addonData?.addon_category_id || '';
              const category = categories.find(cat => cat.id === categoryId);

              return {
                id: addon.id,
                name: addon.name,
                price: addon.price,
                quantity: addon.quantity,
                addon_category_id: categoryId,
                consider_most_expensive: category?.consider_most_expensive,
                price_calculation_type: category?.price_calculation_type,
                select_max: category?.select_max,
                item_select_max: addonData?.select_max,
              };
            })
          : [],
      });
    }
  }

  return choices;
}

/** Reconstrói o estado da tela a partir das escolhas gravadas no carrinho. */
export function fromCartComboChoices(choices: ComboChoice[] | null | undefined): ComboSelection[] {
  if (!Array.isArray(choices)) return [];

  return choices.map(choice => ({
    group_id: choice.group_id,
    item_id: choice.item_id,
    quantity: choice.quantity,
    addons: (choice.addons ?? []).map(addon => ({
      id: addon.id,
      name: addon.name,
      price: addon.price,
      quantity: addon.quantity,
      addon_category_id: addon.addon_category_id,
    })),
  }));
}

/**
 * Valor que as escolhas somam a UMA unidade do combo:
 * Σ (acréscimo da opção + complementos da escolha) × quantidade da escolha.
 * Mesma conta que a API faz no place-order.
 */
export function calculateComboChoicesPrice(choices: ComboChoice[] | null | undefined): number {
  if (!Array.isArray(choices)) return 0;

  return choices.reduce((total, choice) => {
    const additional = Number(choice.additional_price) || 0;
    const addons = calculateCartAddonsPrice(choice.addons ?? []);
    return total + (additional + addons) * (Number(choice.quantity) || 0);
  }, 0);
}

/** `combo_choices` do payload do pedido (contrato PRAT-20). */
export function buildComboChoicesPayload(
  choices: ComboChoice[] | null | undefined
): ComboChoicePayload[] {
  if (!Array.isArray(choices)) return [];

  return choices.map(choice => ({
    group_id: choice.group_id,
    item_id: choice.item_id,
    quantity: choice.quantity,
    addons: (choice.addons ?? []).map(addon => ({
      id: addon.id,
      addon_name: addon.name,
      addon_quantity: addon.quantity,
    })),
  }));
}

/* Montagem em etapas (wizard) --------------------------------------------- */

/**
 * Uma etapa da montagem do combo de escolha:
 * - `group`: um grupo de escolha;
 * - `choice-addons`: uma categoria de complementos do produto escolhido num
 *   grupo personalizável (logo depois do grupo);
 * - `product-addons`: uma categoria de complementos do próprio combo;
 * - `summary`: o resumo, sempre a última.
 */
export type ComboStep =
  | { kind: 'group'; key: string; group: ComboGroup }
  | {
      kind: 'choice-addons';
      key: string;
      group: ComboGroup;
      option: ComboGroupOption;
      category: AddonCategory;
    }
  | { kind: 'product-addons'; key: string; category: AddonCategory }
  | { kind: 'summary'; key: string };

export const COMBO_SUMMARY_STEP_KEY = 'summary';

export function getComboGroupStepKey(groupId: string) {
  return `group:${groupId}`;
}

/**
 * Lista de etapas: cada grupo (na ordem do lojista) → complementos de cada
 * produto escolhido, quando o grupo é personalizável → complementos do próprio
 * combo → resumo. Muda conforme as escolhas (trocar a pizza troca as etapas de
 * complementos que vêm depois dela).
 */
export function buildComboSteps(
  groups: ComboGroup[],
  selections: ComboSelection[],
  productAddonCategories: AddonCategory[] = []
): ComboStep[] {
  const steps: ComboStep[] = [];

  for (const group of getSortedComboGroups(groups)) {
    steps.push({ kind: 'group', key: getComboGroupStepKey(group.id), group });

    if (!isComboGroupCustomizable(group)) continue;

    for (const option of group.options) {
      const selection = findComboSelection(selections, group.id, option.item_id);
      if (!selection || selection.quantity <= 0) continue;

      for (const category of option.addon_categories ?? []) {
        steps.push({
          kind: 'choice-addons',
          key: `addons:${group.id}:${option.item_id}:${category.id}`,
          group,
          option,
          category,
        });
      }
    }
  }

  for (const category of productAddonCategories) {
    steps.push({ kind: 'product-addons', key: `product-addons:${category.id}`, category });
  }

  steps.push({ kind: 'summary', key: COMBO_SUMMARY_STEP_KEY });
  return steps;
}

/** Nome da etapa no topo (ex.: "Tamanho", "Calabresa · Borda recheada"). */
export function getComboStepTitle(step: ComboStep): string {
  switch (step.kind) {
    case 'group':
      return step.group.name;
    case 'choice-addons':
      return `${step.option.name} · ${step.category.name}`;
    case 'product-addons':
      return step.category.name;
    default:
      return 'Resumo';
  }
}

/** Mínimo efetivo da categoria, com a mesma regra de `checkMinimumRequirements`. */
function getAddonCategoryMin(category: AddonCategory, selectedCount: number): number {
  if (category.mandatory === 1) return category.select_min > 0 ? category.select_min : 1;
  // Opcional com mínimo: só vale depois que o cliente começou a escolher.
  return category.select_min > 0 && selectedCount > 0 ? category.select_min : 0;
}

function getAddonCategoryMax(category: AddonCategory): number {
  return category.select_max > 0 ? category.select_max : Infinity;
}

/** Complementos da etapa: os da escolha (grupo personalizável) ou os do combo. */
export function getComboStepAddons(
  step: ComboStep,
  selections: ComboSelection[],
  productAddons: SelectedAddon[] = []
): SelectedAddon[] {
  if (step.kind === 'choice-addons') {
    return findComboSelection(selections, step.group.id, step.option.item_id)?.addons ?? [];
  }
  return step.kind === 'product-addons' ? productAddons : [];
}

/** Categorias da etapa, usadas para resolver a categoria de cada complemento. */
export function getComboStepAddonCategories(
  step: ComboStep,
  productAddonCategories: AddonCategory[] = []
): AddonCategory[] {
  if (step.kind === 'choice-addons') return step.option.addon_categories ?? [step.category];
  if (step.kind === 'product-addons') {
    return productAddonCategories.length > 0 ? productAddonCategories : [step.category];
  }
  return [];
}

/** Regra legível da etapa ("Escolha 1", "Opcional, até 2"…); vazio no resumo. */
export function getComboStepRuleLabel(step: ComboStep): string {
  if (step.kind === 'group') return getComboGroupRuleLabel(step.group);
  if (step.kind === 'summary') return '';

  const { category } = step;
  const required = category.mandatory === 1;
  const min = required ? getAddonCategoryMin(category, 0) : category.select_min;
  return formatChoiceRule(min, getAddonCategoryMax(category), required);
}

export function isComboStepRequired(step: ComboStep): boolean {
  if (step.kind === 'group') return isComboGroupRequired(step.group);
  if (step.kind === 'summary') return false;
  return step.category.mandatory === 1;
}

/** Etapa de escolha única: escolher já avança para a próxima. */
export function isComboStepSingleChoice(step: ComboStep): boolean {
  if (step.kind === 'group') return getMaxChoices(step.group) === 1;
  if (step.kind === 'summary') return false;
  return step.category.select_max === 1;
}

/** Contador da etapa ("2 de 3"): escolhidos e teto (`Infinity` sem teto). */
export function getComboStepCount(
  step: ComboStep,
  selections: ComboSelection[],
  productAddons: SelectedAddon[] = [],
  productAddonCategories: AddonCategory[] = []
): { count: number; max: number } {
  if (step.kind === 'group') {
    return {
      count: getGroupSelectedCount(step.group.id, selections),
      max: getMaxChoices(step.group),
    };
  }
  if (step.kind === 'summary') return { count: 0, max: Infinity };

  return {
    count: getTotalSelectedInCategory(
      step.category.id,
      getComboStepAddons(step, selections, productAddons),
      getComboStepAddonCategories(step, productAddonCategories)
    ),
    max: getAddonCategoryMax(step.category),
  };
}

function pluralItem(count: number) {
  return count === 1 ? 'item' : 'itens';
}

/**
 * Motivo curto para não avançar da etapa ("Escolha 1 opção", "Escolha mais 1
 * item"); vazio quando a etapa está completa. Mesmos mín./máx. que a validação
 * do combo aplica. O resumo não tem motivo próprio (ver `getFirstBlockedComboStep`).
 */
export function getComboStepBlocker(
  step: ComboStep,
  selections: ComboSelection[],
  productAddons: SelectedAddon[] = [],
  productAddonCategories: AddonCategory[] = []
): string {
  if (step.kind === 'summary') return '';

  const { count, max } = getComboStepCount(step, selections, productAddons, productAddonCategories);
  const isGroup = step.kind === 'group';
  const min = isGroup ? getMinChoices(step.group) : getAddonCategoryMin(step.category, count);
  const unit = isGroup ? pluralOption : pluralItem;

  if (count < min) {
    if (count > 0) return `Escolha mais ${min - count} ${unit(min - count)}`;
    return min === max ? `Escolha ${min} ${unit(min)}` : `Escolha pelo menos ${min} ${unit(min)}`;
  }

  if (count > max) return `Escolha no máximo ${max} ${unit(max)}`;
  return '';
}

/**
 * Primeira etapa que impede fechar o combo, com o motivo — é para onde o
 * "Adicionar" leva quando falta algo. `null` quando tudo está completo.
 */
export function getFirstBlockedComboStep(
  steps: ComboStep[],
  selections: ComboSelection[],
  productAddons: SelectedAddon[] = [],
  productAddonCategories: AddonCategory[] = []
): { index: number; message: string } | null {
  for (let index = 0; index < steps.length; index++) {
    const step = steps[index];
    const blocker = getComboStepBlocker(step, selections, productAddons, productAddonCategories);
    if (blocker) return { index, message: `${getComboStepTitle(step)}: ${blocker}` };
  }
  return null;
}

/**
 * Próxima etapa ao tocar "Próximo". Quando o cliente veio do resumo para
 * corrigir uma escolha (`reviewing`), passa só pelas etapas de complementos do
 * mesmo grupo e volta ao resumo.
 */
export function getNextComboStepIndex(
  steps: ComboStep[],
  index: number,
  reviewing = false
): number {
  const last = steps.length - 1;
  const next = Math.min(index + 1, last);
  if (!reviewing) return next;

  const current = steps[index];
  const nextStep = steps[next];
  const groupId =
    current?.kind === 'group' || current?.kind === 'choice-addons' ? current.group.id : null;

  return groupId && nextStep?.kind === 'choice-addons' && nextStep.group.id === groupId
    ? next
    : last;
}
